/**
 * Shared types + presentation constants for the Fleet Actions panel.
 *
 * The shapes mirror `GET /admin/scraper/actions`. Kept in one place so the band and the
 * modal cannot drift from each other.
 */

export interface ThresholdFailure {
  metric: string;
  value: number;
  floor: number;
}

export interface FleetActionCard {
  property_id: string;
  property_id_path: string | null;
  name: string | null;
  code: string;
  bucket: string;
  rank: number;
  guidance: string;
  engine_signal: { matched: string; path: string | null } | null;
  unmatched_text: string[];
  threshold_failures: ThresholdFailure[];
  ready_for_parser: boolean | null;
  scorecard_path: string | null;
  playbook: {
    applied: string | null;
    recommended: string | null;
    carried: boolean;
    path: string | null;
  } | null;
  stored_failure_class: string | null;
  stored_failure_class_path: string | null;
  /** Present ONLY when the receipt's coarse `failureClass` disagrees with the rich
   *  `weeklyFailureClass` (PROOF-0493: 80 receipts disagree outright). The backend
   *  preserves it so the disagreement stays visible; the panel must show it. */
  coarse_failure_class: string | null;
  coarse_failure_class_path: string | null;
  conflict: string | null;
  last_scraped: string | null;
  since: string;
}

export interface FleetActions {
  generated_for: string;
  counts: Record<string, number>;
  bucket_order: string[];
  total: number;
  delta: {
    available: boolean;
    recovered: string[];
    regressed: string[];
    still_stuck: number;
    newly_seen: number;
    left_the_query: number;
    net: number;
  };
  properties: FleetActionCard[];
  classifier: {
    source: string;
    /** sha256 of this repo's mirror text; `pinned` says the copy matches the reviewed
     *  pin. It cannot detect drift in the OTHER repository's classifier. */
    mirror_sha256?: string;
    pinned_sha256?: string;
    pinned?: boolean;
    rules: { code: string; matches: string[] }[];
    pending_pin: { literal: string; claimed_at: string }[];
    note: string;
  };
  baseline_written?: boolean;
}

type ChipColor = "success" | "warning" | "error" | "info" | "default";

export const BUCKET_COLOR: Record<string, ChipColor> = {
  "RE-RUN": "success",
  URL: "info",
  "CHECK-RECEIPT": "info",
  RECIPE: "warning",
  TRIAGE: "warning",
  BLOCKED: "error",
  OK: "default",
};

export const BUCKET_HELP: Record<string, string> = {
  "RE-RUN":
    "The engine itself called these transient — a re-run may help. Free recovery: no code, no decision.",
  URL: "Discovery or DNS failed, or capture drifted off target. Repoint the roster URL first.",
  "CHECK-RECEIPT":
    "A step the bound SIGKILLed. On sightmap_prerequisite this usually means the step SUCCEEDED and then could not exit (PROOF-0495) — the work is done and the label is wrong. Read the step's receipt before treating the property as broken.",
  RECIPE:
    "The engine says deterministic — it states that re-running without changes will not help. Parser or recipe work.",
  TRIAGE: "Quality gate failed with no more specific signal. The scorecard names which threshold.",
  BLOCKED:
    "Login or provider gate. Adversarial surface — do not price these as parser work.",
  OK: "No failure signal in the row.",
};

/** Whole days between an ISO date and today, floored at 0. */
export function daysSince(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const days = Math.floor((now.getTime() - then.getTime()) / 86_400_000);
  return days < 0 ? 0 : days;
}

/**
 * "stuck 14 days" / "since today".
 * Deliberately says "in this bucket", not "broken for": the streak clock restarts when a
 * property changes bucket, so a longer outage can legitimately show a short streak.
 */
export function stuckLabel(since: string, now: Date = new Date()): string {
  const days = daysSince(since, now);
  if (days === null) return "";
  if (days <= 0) return "in this bucket since today";
  if (days === 1) return "in this bucket 1 day";
  return `in this bucket ${days} days`;
}
